import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useGetWlmTransactionDetailQuery } from '@src/store/services/wlm/onlineServices';

const getByPath = (obj: any, path: string) =>
  path.split('.').reduce((acc, key) => acc?.[key], obj);

// Friendly labels matching the legacy WLM gateway's Transaction Set Detail
// layout, in display order.
const SUMMARY_FIELDS: [string, string][] = [
  ['Status At', 'statusAt'],
  ['Transaction File Submitter', 'transactionFileSubmitter'],
  ['Transaction Set Submitter', 'transactionSetSubmitter'],
  ['Transaction Set Id', 'transSetId'],
  ['Transaction File Id', 'transactionFileId'],
  ['Business Process Id', 'businessProcessId'],
  ['SetCreated', 'setCreatedDateTime'],
  ['Status', 'statusCode'],
  ['Inbound Record Count', 'inboundRecordCount'],
  ['Source Record Count', 'sourceRecordCount'],
  ['Transaction Set Record Count', 'transactionSetRecordCount'],
  ['Total Work Units', 'workUnitMetrics.totalWorkUnits'],
  ['Work Units Completed', 'workUnitMetrics.wuCompleted'],
  ['Work Units Started, but Not Completed', 'workUnitMetrics.wuStartedNotCompleted'],
  ['Work Units Not Started', 'workUnitMetrics.wuNotStarted'],
  ['Work Units which Errored', 'workUnitMetrics.wuErrored'],
  ['Work Units which are Late', 'workUnitMetrics.wuLate'],
  ['Average Work Unit Processing Time (m/s)', 'workUnitMetrics.avgProcessingTime'],
  ['Processing Time of Last Work Unit (m/s)', 'workUnitMetrics.lastWuProcessingTime'],
  ['Minutes since Transaction Set was Staged', 'workUnitMetrics.minsSinceStaged'],
  ['Minutes since first Work Unit Started', 'workUnitMetrics.minsFirstWuStarted'],
  ['Minutes since first Work Unit Completed', 'workUnitMetrics.minsFirstWuCompleted'],
  ['Minutes since last Work Unit Started', 'workUnitMetrics.minsLastWuStarted'],
  ['Minutes since last Work Unit Completed', 'workUnitMetrics.minsLastWuCompleted'],
];

const renderLabel = (label: string) => `${label}:`;

type WlmTransactionDetailModalProps = {
  transSetId?: string | number | null;
  bucket?: string | number | null;
  dataSource: string;
  isOpen: boolean;
  onClose: () => void;
};

// Detail drill-down backed by oncallapi/monitor/transaction-detail (ported
// GatewayMonitorWeb's TransactionDetail.do), used by the WLM "Detail" button.
export const WlmTransactionDetailModal = ({
  transSetId,
  bucket,
  dataSource,
  isOpen,
  onClose,
}: WlmTransactionDetailModalProps) => {
  const { data, isFetching, refetch } = useGetWlmTransactionDetailQuery(
    { transSetId: transSetId ?? '', bucket: bucket ?? '', dataSource },
    { skip: !isOpen || !transSetId }
  );

  const workUnits: Array<{
    sequenceNumber?: string | number;
    host?: string;
    errorMessage?: string;
  }> = data?.workUnits ?? [];

  return (
    <ModalDialog
      header="Transaction Set Detail"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      css={{
        'abyss-modal-content-title': {
          padding: '0',
          color:
            'var(--abyss-colors-web-semantic-color-text-content-primaryAlt) !important',
          backgroundColor: '$web.semantic.color.surface.container.primary',
        },
        'abyss-modal-header-container': {
          paddingLeft: '5px',
          paddingRight: '5px',
        },
      }}
    >
      <div>
        <div
          style={{
            maxHeight: '55vh',
            overflowY: 'auto',
            border: '1px solid #cbcccd',
          }}
        >
          {isFetching ? (
            <Text>Loading transaction set detail...</Text>
          ) : data ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {SUMMARY_FIELDS.map(([label, path], index) => {
                    const value = getByPath(data, path);
                    return (
                      <tr
                        key={path}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? '#eaf3fc' : '#ffffff',
                        }}
                      >
                        <td
                          style={{
                            fontWeight: 'bold',
                            padding: '8px 12px',
                            borderBottom: '1px solid #cbcccd',
                            width: '40%',
                          }}
                        >
                          {renderLabel(label)}
                        </td>
                        <td
                          style={{
                            fontWeight: 'bold',
                            color: '#212223',
                            padding: '8px 12px',
                            borderBottom: '1px solid #cbcccd',
                          }}
                        >
                          {value == null || value === '' ? '—' : String(value)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {workUnits.length ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '40%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '35%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {['Sequence Number', 'Host Name', 'Error Message'].map(
                        (header) => (
                          <th
                            key={header}
                            style={{
                              textAlign: 'left',
                              padding: '8px 12px',
                              borderBottom: '2px solid #cbcccd',
                            }}
                          >
                            {header}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {workUnits.map((wu, index) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? '#eaf3fc' : '#ffffff',
                        }}
                      >
                        <td
                          style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #cbcccd',
                          }}
                        >
                          {wu.sequenceNumber}
                        </td>
                        <td
                          style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #cbcccd',
                          }}
                        >
                          {wu.host}
                        </td>
                        <td
                          style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #cbcccd',
                            wordBreak: 'break-word',
                          }}
                        >
                          {wu.errorMessage || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </>
          ) : (
            <Text>No detail available.</Text>
          )}
        </div>
        <div style={{ paddingTop: '10px' }}>
          <Button size="sm" disabled={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>
    </ModalDialog>
  );
};

